import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft, Phone, MapPin, Calendar, TrendingUp, Trophy, XCircle, Clock, User, Building,
  ExternalLink, HeartPulse
} from 'lucide-react';

const API = '/api';

const OUTCOME_BADGE = {
  Won: 'bg-emerald-100 text-emerald-700',
  Lost: 'bg-red-100 text-red-600',
  Open: 'bg-blue-100 text-blue-600',
};

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200">
      <div className={`p-2.5 rounded-lg ${color}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
}

export default function CustomerProfile() {
  const { phone } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const { data: res } = await axios.get(`${API}/customers/profile/${encodeURIComponent(phone)}`, { withCredentials: true });
        setData(res);
      } catch (err) {
        console.error('Failed to fetch profile', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [phone]);

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="customer-profile-loading">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!data?.customer) {
    return (
      <div className="p-6" data-testid="customer-not-found">
        <Button variant="ghost" onClick={() => navigate('/customers')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="text-center py-20 text-slate-400">
          <User className="h-16 w-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">Customer not found</p>
        </div>
      </div>
    );
  }

  const { customer, leads, stats, health } = data;

  const healthColor = health?.health_score >= 70 ? 'emerald' : health?.health_score >= 40 ? 'amber' : 'red';
  const healthLabel = health?.health_score >= 70 ? 'Healthy' : health?.health_score >= 40 ? 'Watch' : 'At Risk';

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="customer-profile-page">
      {/* Back + Header */}
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/customers')} className="mb-3 text-slate-500" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Customers
        </Button>

        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="customer-name">
              {customer.names?.[0] || 'Unknown'}
            </h1>
            {customer.names?.length > 1 && (
              <p className="text-sm text-slate-400 mt-0.5 max-w-[700px] truncate">
                Also known as: {customer.names.slice(1, 4).join(', ')}{customer.names.length > 4 ? ` +${customer.names.length - 4} more` : ''}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
              <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {customer.phone}</span>
              <span className="flex items-center gap-1"><Building className="h-3.5 w-3.5" /> {customer.dealers?.join(', ') || '-'}</span>
              {customer.first_enquiry && (
                <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Since {customer.first_enquiry}</span>
              )}
            </div>
          </div>

          <div className="flex gap-2 flex-wrap max-w-[300px]">
            {customer.kvas?.slice(0, 6).map(k => (
              <Badge key={k} variant="outline" className="text-xs">{k} KVA</Badge>
            ))}
            {customer.kvas?.length > 6 && (
              <Badge variant="outline" className="text-xs text-slate-400">+{customer.kvas.length - 6}</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="customer-stats">
        <StatCard label="Total Leads" value={stats.total_leads} icon={TrendingUp} color="bg-slate-600" />
        <StatCard label="Won" value={stats.won} icon={Trophy} color="bg-emerald-500" />
        <StatCard label="Lost" value={stats.lost} icon={XCircle} color="bg-red-500" />
        <StatCard label="Open" value={stats.open} icon={Clock} color="bg-blue-500" />
        <StatCard label="Conversion" value={`${stats.conversion_rate}%`} icon={TrendingUp} color="bg-violet-500" />
      </div>

      {/* Health Score (Repeat Buyers Only) */}
      {health && (
        <Card className="border-slate-200" data-testid="health-score-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <HeartPulse className={`h-5 w-5 text-${healthColor}-500`} />
                <p className="text-sm font-semibold text-slate-700">Customer Health Score</p>
                <Badge className={`bg-${healthColor}-100 text-${healthColor}-700 text-xs`}>{healthLabel}</Badge>
              </div>
              <span className={`text-3xl font-bold text-${healthColor}-600`} data-testid="health-score-value">{health.health_score}</span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500">Recency</span>
                  <span className="text-xs font-bold text-slate-700">{health.recency_score}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-400 rounded-full transition-all" style={{ width: `${health.recency_score}%` }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500">Frequency</span>
                  <span className="text-xs font-bold text-slate-700">{health.frequency_score}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-violet-400 rounded-full transition-all" style={{ width: `${health.frequency_score}%` }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500">Conversion</span>
                  <span className="text-xs font-bold text-slate-700">{health.conversion_score}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-400 rounded-full transition-all" style={{ width: `${health.conversion_score}%` }} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lifetime bar */}
      {customer.lifetime_days > 0 && (
        <Card className="border-slate-200" data-testid="lifetime-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-slate-600">Customer Lifetime</p>
              <p className="text-sm font-bold text-slate-900">{customer.lifetime_days} days</p>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <span>{customer.first_enquiry}</span>
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-400 to-emerald-400 rounded-full" style={{ width: '100%' }} />
              </div>
              <span>{customer.last_enquiry}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lead History */}
      <Card className="border-slate-200" data-testid="lead-history-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Lead History</CardTitle>
          <p className="text-xs text-slate-400">{leads.length} leads found for this customer</p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="text-xs">Enquiry No</TableHead>
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs">Dealer</TableHead>
                  <TableHead className="text-xs">KVA</TableHead>
                  <TableHead className="text-xs">Stage</TableHead>
                  <TableHead className="text-xs">Outcome</TableHead>
                  <TableHead className="text-xs">Employee</TableHead>
                  <TableHead className="text-xs">Last Followup</TableHead>
                  <TableHead className="text-xs">Remark</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow
                    key={lead.lead_id}
                    data-testid={`lead-row-${lead.lead_id}`}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <TableCell>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/leads?search=${lead.enquiry_no}`); }}
                        className="text-sm font-mono text-blue-600 hover:underline"
                        data-testid={`lead-link-${lead.lead_id}`}
                      >
                        {lead.enquiry_no || '-'}
                      </button>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{lead.enquiry_date || '-'}</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[150px] truncate">{lead.dealer || '-'}</TableCell>
                    <TableCell className="text-sm">{lead.kva || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-600">{lead.enquiry_stage || '-'}</TableCell>
                    <TableCell>
                      <Badge className={`${OUTCOME_BADGE[lead.outcome] || 'bg-slate-100 text-slate-600'} text-xs`}>
                        {lead.outcome}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[120px] truncate">{lead.employee_name || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-500">{lead.last_followup_date || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-400 max-w-[200px] truncate">{lead.last_followup_remark || lead.remarks || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
